import React, { ComponentPropsWithoutRef, memo } from 'react'
import {
  Controller,
  ControllerProps,
  FieldErrors,
  FieldValues,
  get,
} from 'react-hook-form'

import { Input } from '@surglogs-app/ui'

import { DefaultFormField, DefaultFormFieldProps } from './DefaultFormField'

interface InputFieldProps<TValuesModel extends FieldValues>
  extends Omit<ComponentPropsWithoutRef<typeof Input>, 'name' | 'defaultValue'>,
    Pick<
      ControllerProps<TValuesModel>,
      'shouldUnregister' | 'control' | 'name' | 'defaultValue'
    > {
  errors?: FieldErrors<any> | Record<string, any>
  warnings?: Record<string, any>
  formFieldProps?: Omit<
    DefaultFormFieldProps,
    'name' | 'errors' | 'warnings' | 'children'
  >
}

const Component = <TValuesModel extends FieldValues = FieldValues>({
  name,
  defaultValue,
  control,
  errors,
  warnings,
  invalid,
  warning,
  shouldUnregister,
  ...rest
}: InputFieldProps<TValuesModel>) => (
  <Controller<TValuesModel>
    {...{
      name,
      control,
      defaultValue,
      shouldUnregister,
      // onChange,
      render: ({ field }) => {
        return (
          <Input
            {...rest}
            {...field}
            {...{
              invalid: invalid ?? !!get(errors || {}, name),
              warning: warning ?? !!get(warnings || {}, name),
            }}
          />
        )
      },
    }}
  />
)

/**
 * Input field to work with react hook form
 *
 * NOTE: if you pass formFieldProps, you dont need to pass invalid/warning states,
 * it will done automatically by DefaultFormField
 *
 */
export const InputField = memo(
  <TValuesModel extends FieldValues = FieldValues>({
    formFieldProps = undefined,
    ...rest
  }: InputFieldProps<TValuesModel>) => {
    if (formFieldProps) {
      return (
        <DefaultFormField
          {...{
            ...formFieldProps,
            name: rest.name,
            errors: rest.errors,
            warnings: rest.warnings,
            invalid: rest.invalid,
          }}>
          <Component {...rest} />
        </DefaultFormField>
      )
    }

    return <Component {...rest} />
  },
) as typeof Component
